import React from 'react';
import { motion } from 'framer-motion';
import { ChevronDownIcon } from 'lucide-react';
const agentBadges = [{
  name: 'Cursor',
  type: 'IDE Agent',
  color: '#A855F7',
  delay: 0,
  floatClass: 'float-1',
  position: 'top-[15%] left-[8%]'
}, {
  name: 'Claude Code',
  type: 'Terminal Agent',
  color: '#F97316',
  delay: 0.2,
  floatClass: 'float-2',
  position: 'top-[20%] right-[10%]'
}, {
  name: 'Devin',
  type: 'Autonomous Agent',
  color: '#3B82F6',
  delay: 0.4,
  floatClass: 'float-3',
  position: 'bottom-[25%] left-[6%]'
}, {
  name: 'GitHub Copilot',
  type: 'Assisted Agent',
  color: '#10B981',
  delay: 0.6,
  floatClass: 'float-4',
  position: 'bottom-[20%] right-[8%]'
}];
export function HeroSection() {
  return <section className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden bg-[#080C14]" data-id="element-204">
      {/* Grid background */}
      <div className="absolute inset-0 grid-bg opacity-60" data-id="element-205" />

      {/* Radial gradient overlay */}
      <div className="absolute inset-0 bg-radial-gradient" style={{
      background: 'radial-gradient(ellipse 80% 60% at 50% 40%, rgba(0,212,255,0.04) 0%, transparent 70%)'
    }} data-id="element-206" />

      {/* Floating agent badges */}
      {agentBadges.map(badge => <motion.div key={badge.name} className={`absolute hidden md:flex items-center gap-2.5 px-4 py-2.5 rounded-xl border backdrop-blur-sm ${badge.floatClass} ${badge.position}`} style={{
      backgroundColor: 'rgba(13, 20, 33, 0.85)',
      borderColor: `${badge.color}30`
    }} initial={{
      opacity: 0,
      scale: 0.8
    }} animate={{
      opacity: 1,
      scale: 1
    }} transition={{
      delay: badge.delay + 0.8,
      duration: 0.5
    }} data-id="element-207">
          <span className="pulse-dot w-2 h-2 rounded-full flex-shrink-0" style={{
        backgroundColor: badge.color
      }} data-id="element-208" />
          <div data-id="element-209">
            <div className="text-white text-sm font-semibold leading-tight" data-id="element-210">
              {badge.name}
            </div>
            <div className="text-xs leading-tight" style={{
          color: badge.color
        }} data-id="element-211">
              {badge.type}
            </div>
          </div>
          <div className="ml-1 px-1.5 py-0.5 rounded text-[10px] font-bold tracking-wide" style={{
        backgroundColor: `${badge.color}20`,
        color: badge.color
      }} data-id="element-212">
            ACTIVE
          </div>
        </motion.div>)}

      {/* Main content */}
      <div className="relative z-10 text-center px-6 max-w-5xl mx-auto" data-id="element-213">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="mb-6" data-id="element-214">
          <span className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.25em] text-cyan-400 uppercase" data-id="element-215">
            <span className="w-8 h-px bg-cyan-400/50" data-id="element-216" />
            Waydev Intelligence
            <span className="w-8 h-px bg-cyan-400/50" data-id="element-217" />
          </span>
        </motion.div>

        <motion.h1 initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.7,
        delay: 0.1
      }} className="text-6xl md:text-8xl lg:text-9xl font-black text-white tracking-tight leading-none mb-6" data-id="element-218">
          Measure
          <br data-id="element-219" />
          <span className="text-transparent bg-clip-text" style={{
          backgroundImage: 'linear-gradient(135deg, #00D4FF 0%, #3B82F6 100%)'
        }} data-id="element-220">
            AI Agents
          </span>
        </motion.h1>

        <motion.p initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.3
      }} className="text-xl md:text-2xl text-gray-400 font-light mb-4 tracking-wide" data-id="element-221">
          Track Autonomous Agent Behavior
        </motion.p>

        <motion.p initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.4
      }} className="text-base text-gray-500 max-w-2xl mx-auto mb-12" data-id="element-222">
          Detect Cursor agents, Claude Code, Devin, and Copilot. Identify
          autonomous vs assisted contributions. Measure the real impact of AI on
          your engineering org.
        </motion.p>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.5
      }} className="flex flex-wrap items-center justify-center gap-3" data-id="element-223">
          {['Cursor', 'Claude Code', 'Devin', 'GitHub Copilot'].map((agent, i) => <span key={agent} className="px-3 py-1.5 rounded-full text-sm font-medium border border-[#1E2D45] text-gray-300 bg-[#0D1421]" data-id="element-224">
                {agent}
              </span>)}
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.65
      }} className="mt-8" data-id="element-225">
          <a href="https://waydev.co/demo" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl text-sm font-bold text-[#080C14] transition-all duration-200 hover:scale-105 active:scale-95" style={{
          background: 'linear-gradient(135deg, #00D4FF 0%, #3B82F6 100%)',
          boxShadow: '0 0 24px rgba(0, 212, 255, 0.35)'
        }} data-id="element-226">
            Get a Demo
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-227">
              <path d="M1 7h12M7 1l6 6-6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" data-id="element-228" />
            </svg>
          </a>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-gray-600" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 1.5
    }} data-id="element-229">
        <span className="text-xs tracking-widest uppercase" data-id="element-230">Scroll</span>
        <motion.div animate={{
        y: [0, 6, 0]
      }} transition={{
        repeat: Infinity,
        duration: 1.5
      }} data-id="element-231">
          <ChevronDownIcon className="w-4 h-4" data-id="element-232" />
        </motion.div>
      </motion.div>
    </section>;
}